import { useEffect, useState } from 'react';
import { Bluetooth, CircleX, Lightbulb, Link2, RefreshCw, Unplug } from 'lucide-react';
import { DEFAULT_SMART_BOARD_CONFIG, smartBoard, type SmartBoardConfig, type SmartBoardStatus } from '../core/smartBoard';
import type { FocusMode, PatternProject } from '../types';

const STORAGE_KEY = 'yxmpd-smart-board-config-v1';

function readConfig(): SmartBoardConfig {
  try { return { ...DEFAULT_SMART_BOARD_CONFIG, ...JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') }; }
  catch { return DEFAULT_SMART_BOARD_CONFIG; }
}

interface SmartBoardPanelProps {
  project: PatternProject;
  selectedColor: number | null;
  focusMode: FocusMode;
  canAccessHardwareBeta: boolean;
  onClose: () => void;
  onConnectionChange: (connected: boolean) => void;
}

export function SmartBoardPanel(props: SmartBoardPanelProps) {
  if (!props.canAccessHardwareBeta) {
    return <div className="modal-backdrop editor-dialog-layer" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <section className="editor-dialog smart-board-dialog smart-board-beta" role="dialog" aria-modal="true" aria-labelledby="smart-board-title">
        <button className="editor-dialog-close" type="button" onClick={props.onClose} aria-label="关闭"><CircleX size={19} /></button>
        <span className="editor-dialog-icon bluetooth"><Bluetooth size={24} /></span>
        <h2 id="smart-board-title">智能豆板</h2>
        <strong>测试阶段</strong>
        <p>该功能正在内部测试，暂未开放使用。</p>
      </section>
    </div>;
  }
  return <SmartBoardDeveloperPanel project={props.project} selectedColor={props.selectedColor} focusMode={props.focusMode} onClose={props.onClose} onConnectionChange={props.onConnectionChange} />;
}

function SmartBoardDeveloperPanel({ project, selectedColor, focusMode, onClose, onConnectionChange }: Omit<SmartBoardPanelProps, 'canAccessHardwareBeta'>) {
  const [config, setConfig] = useState(readConfig);
  const [status, setStatus] = useState<SmartBoardStatus>(smartBoard.getStatus());
  const [busy, setBusy] = useState<'connect' | 'sync' | 'test' | ''>('');
  const [message, setMessage] = useState('');
  const support = smartBoard.getSupport();

  useEffect(() => smartBoard.subscribe((next) => { setStatus(next); onConnectionChange(next.connected); }), [onConnectionChange]);

  async function run(kind: 'connect' | 'sync' | 'test', task: () => Promise<void>, success: string) {
    setBusy(kind); setMessage('');
    try { await task(); setMessage(success); }
    catch (reason) { setMessage(reason instanceof Error ? reason.message : '设备操作失败'); }
    finally { setBusy(''); }
  }

  return <div className="modal-backdrop editor-dialog-layer" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="editor-dialog smart-board-dialog" role="dialog" aria-modal="true" aria-labelledby="smart-board-title">
      <button className="editor-dialog-close" type="button" onClick={onClose} aria-label="关闭"><CircleX size={19} /></button>
      <div className="editor-dialog-heading"><span className="editor-dialog-icon bluetooth"><Bluetooth size={22} /></span><div><p className="eyebrow">ESP32 · YXMPD LIGHT V1</p><h2 id="smart-board-title">智能豆板灯光联动</h2><p>连接后可同步整张图纸；切换当前色和标记完成会实时发送增量指令。</p></div></div>

      <div className={`device-status ${status.connected ? 'connected' : ''}`}><span><i /><strong>{status.connected ? status.deviceName : '尚未连接设备'}</strong><small>{status.connected ? `${status.transport === 'native' ? '原生桥接' : 'Web Bluetooth'} · 灯光联动已就绪` : support.reason || '选择附近的 ESP32 智能豆板'}</small></span>{status.connected && <button type="button" onClick={() => void smartBoard.disconnect()}><Unplug size={16} />断开</button>}</div>

      <div className="device-config">
        <label htmlFor="ble-service">BLE Service UUID<input id="ble-service" value={config.serviceUuid} onChange={(event) => setConfig((current) => ({ ...current, serviceUuid: event.target.value.trim() }))} spellCheck={false} /></label>
        <label htmlFor="ble-write">Write Characteristic UUID<input id="ble-write" value={config.writeCharacteristicUuid} onChange={(event) => setConfig((current) => ({ ...current, writeCharacteristicUuid: event.target.value.trim() }))} spellCheck={false} /></label>
        <small>默认使用 ESP32 常见的 Nordic UART Service。硬件固件只要按行接收 UTF-8 JSON，并支持 <b>yxmpd-light-v1</b> 即可。</small>
      </div>

      <div className="device-actions">
        {!status.connected ? <button className="primary-button" type="button" disabled={!support.available || !!busy || !config.serviceUuid || !config.writeCharacteristicUuid} onClick={() => void run('connect', async () => { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); await smartBoard.connect(config); await smartBoard.sendFocus(project, selectedColor, focusMode); }, '设备已连接，可以同步图纸了')}><Link2 size={18} />{busy === 'connect' ? '正在连接…' : '连接附近设备'}</button> : <>
          <button className="primary-button" type="button" disabled={!!busy} onClick={() => void run('sync', async () => { await smartBoard.syncProject(project); await smartBoard.sendFocus(project, selectedColor, focusMode); }, '图纸与当前灯光焦点已同步')}><RefreshCw className={busy === 'sync' ? 'spin' : ''} size={18} />{busy === 'sync' ? '正在同步…' : '同步当前图纸'}</button>
          <button className="secondary-button" type="button" disabled={!!busy} onClick={() => void run('test', () => smartBoard.testLights(), '已发送 1.8 秒灯光测试')}><Lightbulb size={17} />测试灯光</button>
        </>}
      </div>
      {message && <p className="device-message" role="status">{message}</p>}
      <p className="editor-dialog-note">说明：Web Bluetooth 需要 HTTPS，iPad Safari 当前通常不开放该能力；未来原生 APP 会自动使用 <code>YXMPDNativeBluetooth</code> 桥接，但仍复用同一套页面和协议。</p>
    </section>
  </div>;
}
